import { useState, useEffect } from 'react';
import { Container, Modal, FormGroup, Button, Spinner, FormControl, FormText, Row, Col } from 'react-bootstrap';
import '../modal.css';
import { emailUtils } from '../utils/helpers';
import { resetPassword as amplifyResetPassword,
         confirmResetPassword } from 'aws-amplify/auth';
const allowedSpecialCharacters = "^ $ * . [ ] { } ( ) ? \" ! @ # % & / \\ , > < ' : ; | _ ~ ` - + =";
const PASSWORD_HELP_TEXT = () => (
    <div>
        <p><b>Password Rules:</b></p>
        <ul style={{ listStyleType: 'circle' }}>
            <li>Must be at least eight characters in length.</li>
            <li>Cannot contain spaces.</li>
            <li>Must contain one uppercase character.</li>
            <li>Must contain one lowercase character.</li>
            <li>Must contain one number.</li>
            <li>Must contain one special character.</li>
            <li>Allowed special characters: {allowedSpecialCharacters}</li>
        </ul>
    </div>
);

//8 characters, 1 Uppercase, 1 Lowercase, 1 number, 1 special character
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.[\]\\{}()?"!@#%&/,><':;|_\+\-\=~`])\S{8,99}$/;
const RESET_ERROR_TEXT = "Error resetting password. Please check your code and try again.";

function ResetPasswordModal(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [showResetModal, setShowResetModal] = useState(false);
    const [showFinishedModal, setShowFinishedModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [isConfirmedPasswordValid, setIsConfirmedPasswordValid] = useState(false);
    const [requestErrorText, setRequestErrorText] = useState("");
    const [infoBlockText, setInfoBlockText] = useState("");


    //reset when modal is shown
    useEffect(() => {
        if (props.showConfirmationModal === true) {
            setResetEmail('');
            setIsEmailValid(false);
            setShowFinishedModal(false);
            setRequestErrorText('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.showConfirmationModal]);

    const tryHideModal = (e) => {
        props.setShowConfirmationModal(false)
    }

    const validateField = (fieldText) => {
        return fieldText.match(PASSWORD_PATTERN) ? true : false;
    }

    const validateEmailField = (fieldText) => {
        //don't allow empty or only whitespace
        return fieldText.trim().length === 0 ? false : true;
    }

    const handleChangePassword = (event) => {
        setPassword(event.target.value);
        setIsPasswordValid(validateField(event.target.value));
    }

    const handleChangeConfirmedPassword = (event) => {
        setConfirmPassword(event.target.value);
        setIsConfirmedPasswordValid(validateField(event.target.value));
    }

    //step 1: send the reset code
    async function onConfirmation() {
        if (!isEmailValid) return;
        setIsLoading(true);
        setInfoBlockText('');
        try {
            const { nextStep } = await amplifyResetPassword({ username: resetEmail.trim() });
            // nextStep.resetPasswordStep will usually be 'CONFIRM_RESET_PASSWORD_WITH_CODE'
            // nextStep.codeDeliveryDetails has where the code was sent (email/phone).
            handleResetCodeSendSuccess(nextStep?.codeDeliveryDetails);
        } catch (err) {
            handleResetCodeSendError(err);
        } finally {
            setIsLoading(false);
        }
    }

      // Step 2: confirm code + new password
    async function onSubmitNewPassword() {
        if (!isConfirmedPasswordValid || !isPasswordValid || password !== confirmPassword || resetCode.trim().length < 1) return;

        setIsLoading(true);
        setInfoBlockText('');
        try {
            await confirmResetPassword({
            username: resetEmail.trim(),
            confirmationCode: resetCode.trim(),
            newPassword: password,
            });
            handleResetSuccess();
        } catch (err) {
            handleResetError(err);
        } finally {
            setIsLoading(false);
        }
    }

    //"enter" key handling
    const handleEnterPress = (e) => {
        if(props.showConfirmationModal){
            emailUtils.validateEmailCase(e);
        }
        
        const code = e.keyCode || e.which;
        if (code === 13) {
            if(props.showConfirmationModal){
                onConfirmation();
            }
            else if(showResetModal){
                //handleSubmitWithEnter();
                onSubmitNewPassword();
            }
        }
    }

    function handleResetCodeSendSuccess(data) {
        props.setShowConfirmationModal(false);
        setShowResetModal(true);
    }

    function handleResetCodeSendError(err) {
        if (err.code === "UserNotFoundException") {
            setRequestErrorText("Email address not found. Contact gayli.craver@cepedasystems.com for assistance.");
        }
        else if (err.code === "NotAuthorizedException") {
            setRequestErrorText("User does not have a password set. Check your email for a temporary password. Contact gayli.craver@cepedasystems.com for assistance.")
        }
        else if (err.code === "LimitExceededException") {
            setRequestErrorText("Reset attempts exceeded. Try again later. Contact gayli.craver@cepedasystems.com for assistance.");
        }
        else {
            setRequestErrorText("Failed to send reset code. Contact gayli.craver@cepedasystems.com for assistance.");
        }
    }

    function handleResetSuccess() {
        setShowResetModal(false);
        setShowFinishedModal(true);
    }

    function handleResetError(err) {
        if (err?.name === "CodeMismatchException") {
        setInfoBlockText("Invalid verification code provided, please try again. Contact gayli.craver@cepedasystems.com for assistance.");
        } else if (err?.name === "ExpiredCodeException") {
        setInfoBlockText("Verification code expired. Please request a new code.");
        } else if (err?.name === "InvalidPasswordException") {
        setInfoBlockText("Password does not meet policy requirements.");
        } else {
        setRequestErrorText("Failed to reset password. Contact gayli.craver@cepedasystems.com for assistance.");
        }
    }

    const textAreaErrorStyle = {
        border: "1px solid red",
        width: "100%",
        paddingLeft: '5px'
    };

    const textAreaStyle = {
        width: "100%",
        paddingLeft: '5px'
    };

    return (
    <Container>
      {/* Step 1 modal: enter email to send code */}
      <Modal
        bsSize="small"
        show={props.showConfirmationModal}
        onHide={tryHideModal}
        animation
        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
        size='sm'
      >
        <Modal.Header closeButton disabled={showFinishedModal}>
          <Modal.Title style={{ fontSize: '18px', color: 'dimgray' }}>
            <img src={require('../assets/icon.png')} width="30" height="30" className="d-inline-block align-middle" alt="CAP logo" />{' '}
            Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col md="auto">
              <FormGroup>
                <FormText style={{ fontSize: '16px', color: 'dimgray' }}>
                  Enter your email address to send a recovery code.
                </FormText>
              </FormGroup>
            </Col>
          </Row>
          <br />
          <Row>
            <Col md="12">
              <FormGroup controlId="username"> {/* controlId (camelCase) in newer react-bootstrap */}
                <FormControl
                  autoFocus
                  id='username'
                  type="email"
                  placeholder="Email Address"
                  onChange={(e) => { setResetEmail(e.target.value); setIsEmailValid(validateEmailField(e.target.value)); }}
                  style={isEmailValid ? textAreaStyle : textAreaErrorStyle}
                  onKeyDown={handleEnterPress} // ✅ onKeyDown instead of onKeyPress
                  onPaste={emailUtils.validateEmailCase}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className="justify-content-md-center">
            <Col md="12">
              <FormGroup>
                <FormText className="text-center" style={{ color: 'red', fontSize: '14px' }}>
                  {requestErrorText}
                </FormText>
              </FormGroup>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'rgb(0,168,168)' }}>
          <Button variant="outline-light" size='sm' disabled={!isEmailValid} onClick={onConfirmation}>
            {isLoading ? <Spinner as="span" animation="border" size='sm' role="status" aria-hidden="true" /> : "Ok"}
          </Button>
          <Button variant="outline-light" size='sm' onClick={tryHideModal}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* Step 2 modal: enter code + new password */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)} animation disabled={showFinishedModal}>
        <Modal.Header closeButton disabled={showFinishedModal}>
          <Modal.Title style={{ fontSize: '18px', color: 'dimgray' }}>
            <img src={require('../assets/icon.png')} width="30" height="30" className="d-inline-block align-middle" alt="CAP logo" />{' '}
            Reset Password
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col md="auto">
              <FormGroup>
                <FormText style={{ fontSize: '16px', color: 'dimgray' }}>
                  A reset code has been sent to {resetEmail}.
                </FormText>
                <FormText style={{ fontSize: '16px', color: 'dimgray' }}>
                  Please complete the form to reset your password.
                </FormText>
              </FormGroup>
            </Col>
          </Row>
          <br />
          <Row>
            <Col />
            <Col md="auto">
              <FormGroup controlId="resetCode">
                <FormControl
                  autoFocus
                  id='resetCode'
                  type="text"                         // ✅ 'code' is not a standard input type
                  placeholder="Reset Code"
                  onChange={(e) => setResetCode(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col />
          </Row>
          <Row>
            <Col />
            <Col md="auto">
              <FormGroup controlId="password">
                <FormControl
                  id="password"
                  type="password"
                  placeholder="New Password"
                  onChange={handleChangePassword}
                  style={isPasswordValid ? textAreaStyle : textAreaErrorStyle}
                />
              </FormGroup>
            </Col>
            <Col />
          </Row>
          <Row>
            <Col />
            <Col md="auto">
              <FormGroup controlId="confirmPassword">
                <FormControl
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm Password"
                  onChange={handleChangeConfirmedPassword}
                  onKeyDown={handleEnterPress}       // ✅
                  style={isConfirmedPasswordValid ? textAreaStyle : textAreaErrorStyle}
                />
              </FormGroup>
            </Col>
            <Col />
          </Row>
          <Row>
            <Col />
            <Col md="auto">
              <FormGroup>
                <FormText className="text-center" style={{ color: 'red', fontSize: '14px', width: '500px' }}>
                  {infoBlockText}
                </FormText>
                <FormText>{PASSWORD_HELP_TEXT()}</FormText>
              </FormGroup>
            </Col>
            <Col />
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'rgb(0,168,168)' }}>
          <Button
            variant="outline-light"
            size='sm'
            disabled={!isConfirmedPasswordValid || !isPasswordValid || password !== confirmPassword || resetCode.trim().length < 1}
            onClick={onSubmitNewPassword}            // ✅ confirm step
          >
            {isLoading
              ? <Spinner as="span" animation="border" size='sm' role="status" aria-hidden="true" />
              : "Reset"}
          </Button>{' '}
          <Button variant="outline-light" size='sm' onClick={() => setShowResetModal(false)}>Cancel</Button>{' '}
        </Modal.Footer>
      </Modal>

      {/* Success modal */}
      <Modal
        bsSize="small"
        show={showFinishedModal}
        onHide={() => setShowFinishedModal(false)}
        animation
        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
        size='sm'
      >
        <Modal.Header closeButton disabled={showFinishedModal}>
          <Modal.Title style={{ fontSize: '18px', color: 'dimgray' }}>
            <img src={require('../assets/icon.png')} width="30" height="30" className="d-inline-block align-middle" alt="CAP logo" />{' '}
            Success
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col />
            <Col md="auto">
              <FormGroup>
                <FormText style={{ fontSize: '16px', color: 'dimgray' }}>Password reset successfully.</FormText>
                <FormText style={{ fontSize: '16px', color: 'dimgray' }}>Please log in.</FormText>
              </FormGroup>
            </Col>
            <Col />
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'rgb(0,168,168)' }}>
          <Button variant="outline-light" size='sm' onClick={() => { setShowFinishedModal(false); window.location.reload(); }}>
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ResetPasswordModal;